// Chrome before 84 and Safari before 14.1 lay flexbox out without `gap`, so every gap-* row and
// column collapses and buttons, icons and labels touch. Where that happens, this gives the
// children of flex containers the same spacing as margins. Browsers with flex gap never run it.

const MARKER = "data-flex-gap";
const RESCAN_DELAY_MS = 150;

const END = { row: "marginRight", "row-reverse": "marginLeft", column: "marginBottom", "column-reverse": "marginTop" };
const START = { row: "marginLeft", "row-reverse": "marginRight", column: "marginTop", "column-reverse": "marginBottom" };
const MIRROR = { marginLeft: "marginRight", marginRight: "marginLeft", marginTop: "marginTop", marginBottom: "marginBottom" };

/** Measures a two-item column with a 1px row gap: it is 1px tall only where flex gap works. */
export const supportsFlexGap = (doc = document) => {
  const flex = doc.createElement("div");
  flex.style.display = "flex";
  flex.style.flexDirection = "column";
  flex.style.rowGap = "1px";
  flex.appendChild(doc.createElement("div"));
  flex.appendChild(doc.createElement("div"));
  doc.body.appendChild(flex);
  const supported = flex.scrollHeight === 1;
  doc.body.removeChild(flex);
  return supported;
};

/**
 * Which margin stands in for each gap between neighbouring flex items. The space goes on the end
 * of an element, or on the start of the next one when the element side is bare text.
 */
export const spacingPlan = (items, flexDirection, direction) => {
  const flow = END[flexDirection] ? flexDirection : "row";
  const side = (sides) => (direction === "rtl" ? MIRROR[sides[flow]] : sides[flow]);
  return items.slice(0, -1).reduce((plan, item, index) => {
    if (item.element) return plan.concat({ index, side: side(END) });
    if (items[index + 1].element) return plan.concat({ index: index + 1, side: side(START) });
    return plan;
  }, []);
};

/** Indexes of the items below the first line of a wrapped row, from their top edges. */
export const laterLineItems = (tops) =>
  tops.reduce((later, top, index) => (top > tops[0] + 1 ? later.concat(index) : later), []);

const inFlowItems = (container) =>
  Array.prototype.filter.call(container.childNodes, (node) => {
    if (node.nodeType === 3) return node.textContent.trim() !== "";
    if (node.nodeType !== 1) return false;
    const style = window.getComputedStyle(node);
    return style.display !== "none" && style.position !== "absolute" && style.position !== "fixed";
  });

const clearPrevious = (doc) =>
  Array.prototype.forEach.call(doc.querySelectorAll(`[${MARKER}]`), (node) => {
    node.getAttribute(MARKER).split(",").forEach((side) => {
      node.style[side] = "";
    });
    node.removeAttribute(MARKER);
  });

// Leaves margins the page already sets (including auto margins) as they are.
const setMargin = (node, side, value) => {
  if (node.style[side] || window.getComputedStyle(node)[side] !== "0px") return;
  node.style[side] = value;
  const marked = node.getAttribute(MARKER);
  node.setAttribute(MARKER, marked ? `${marked},${side}` : side);
};

const queryAll = (doc, selectors) =>
  selectors.reduce((found, selector) => {
    try {
      Array.prototype.forEach.call(doc.querySelectorAll(selector), (node) => found.add(node));
    } catch (e) {
      // A selector this browser can't parse: its rule doesn't apply here either.
    }
    return found;
  }, new Set());

/** One pass over every element matched by the gap selectors. */
export const applyFlexGapFallback = (doc, selectors) => {
  clearPrevious(doc);
  const wrapped = [];
  queryAll(doc, selectors).forEach((container) => {
    const style = window.getComputedStyle(container);
    if (style.display !== "flex" && style.display !== "inline-flex") return;
    const column = style.flexDirection.indexOf("column") === 0;
    const gap = column ? style.rowGap : style.columnGap;
    const items = inFlowItems(container);
    if (gap && gap !== "normal" && gap !== "0px") {
      const flags = items.map((node) => ({ element: node.nodeType === 1 }));
      spacingPlan(flags, style.flexDirection, style.direction).forEach(({ index, side }) => setMargin(items[index], side, gap));
    }
    if (!column && style.flexWrap !== "nowrap" && style.rowGap && style.rowGap !== "normal" && style.rowGap !== "0px") {
      wrapped.push({ items: items.filter((node) => node.nodeType === 1), rowGap: style.rowGap });
    }
  });
  // Lines are only known once the row spacing is in place; one read pass, then one write pass.
  wrapped
    .map(({ items, rowGap }) => ({ items, rowGap, later: laterLineItems(items.map((node) => node.getBoundingClientRect().top)) }))
    .forEach(({ items, rowGap, later }) => later.forEach((index) => setMargin(items[index], "marginTop", rowGap)));
};

/** Selectors of every readable style rule that sets a gap, including those inside media queries. */
export const gapSelectors = (doc) => {
  const selectors = new Set();
  const visit = (rules) =>
    Array.prototype.forEach.call(rules || [], (rule) => {
      if (rule.cssRules) visit(rule.cssRules);
      const { style, selectorText } = rule;
      if (!style || !selectorText) return;
      if (style.getPropertyValue("row-gap") || style.getPropertyValue("column-gap") || style.getPropertyValue("gap")) {
        selectorText.split(",").forEach((selector) => selectors.add(selector.trim()));
      }
    });
  Array.prototype.forEach.call(doc.styleSheets, (sheet) => {
    try {
      visit(sheet.cssRules);
    } catch (e) {
      // Cross-origin stylesheets (web fonts) can't be read and set no gaps.
    }
  });
  return Array.from(selectors);
};

/** Keeps flex spacing right as React renders and the window resizes, where flex gap is missing. */
export const startFlexGapFallback = (doc = document) => {
  if (typeof MutationObserver === "undefined" || supportsFlexGap(doc)) return;
  let sheets = -1;
  let selectors = [];
  let timer = null;
  const run = () => {
    timer = null;
    if (doc.styleSheets.length !== sheets) {
      sheets = doc.styleSheets.length;
      selectors = gapSelectors(doc);
    }
    applyFlexGapFallback(doc, selectors);
  };
  const schedule = () => {
    if (!timer) timer = setTimeout(run, RESCAN_DELAY_MS);
  };
  new MutationObserver(schedule).observe(doc.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
  window.addEventListener("resize", schedule);
  // Lazily loaded stylesheets add rules after they load, not when their <link> is added.
  doc.addEventListener(
    "load",
    (event) => {
      if (event.target && event.target.tagName === "LINK") schedule();
    },
    true
  );
  schedule();
};
